// Per-browser settings persisted to localStorage.
//
// Unlike the standalone niu-web, this build is served by the niu-ws host
// itself — so the WebSocket endpoint is same-origin and derived from
// `location`; only the auth token is user-supplied. `VITE_WS_URL` overrides
// the endpoint for local dev against a remote backend.

import { create } from 'zustand';
import { newUUID } from './uuid';

const LS_TOKEN = 'niu.token';
const LS_DEVICE = 'niu.deviceId';
const LS_THEME = 'niu.theme';
const LS_ACTIVE_AGENT = 'niu.activeAgent';
const LS_SIDEBAR_PINNED = 'niu.sidebarPinned';
const LS_SIDEBAR_WIDTH = 'niu.sidebarWidth';

export const SIDEBAR_WIDTH_MIN = 200;
export const SIDEBAR_WIDTH_MAX = 480;
export const SIDEBAR_WIDTH_DEFAULT = 256;

export type ThemeMode =
  | 'system'
  | 'light'
  | 'dark'
  | 'everforest'
  | 'everforest-light'
  | 'espresso'
  | 'espresso-light'
  | 'lavendel'
  | 'lavendel-dark';

const THEME_MODES: readonly ThemeMode[] = [
  'system',
  'light',
  'dark',
  'everforest',
  'everforest-light',
  'espresso',
  'espresso-light',
  'lavendel',
  'lavendel-dark',
];

interface SettingsState {
  /** Same-origin WS endpoint (or VITE_WS_URL in dev). Derived, not user-editable. */
  wssUrl: string;
  token: string;
  deviceId: string;
  themeMode: ThemeMode;
  hasCredentials: boolean;
  /** When true, the sidebar renders as a permanent left column (desktop). */
  sidebarPinned: boolean;
  /** Pinned-sidebar width in px. Clamped to [MIN, MAX]. */
  sidebarWidth: number;
  setToken: (token: string) => void;
  clearToken: () => void;
  setThemeMode: (mode: ThemeMode) => void;
  setSidebarPinned: (pinned: boolean) => void;
  setSidebarWidth: (px: number) => void;
  /** Last active agent id, restored on next page load. */
  loadActiveAgent: () => string | null;
  saveActiveAgent: (agentId: string) => void;
}

/** The WS endpoint: same origin as the page, unless overridden for dev. */
function deriveWssUrl(): string {
  const override = import.meta.env.VITE_WS_URL;
  if (override && override.length > 0) return override;
  const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${location.host}/`;
}

/** Adopt a `?token=` query param on first load, then strip it from the URL so
 *  the credential doesn't linger in history or get shared via copy-paste. */
function adoptTokenFromUrl(): void {
  try {
    const url = new URL(location.href);
    const t = url.searchParams.get('token');
    if (t) {
      localStorage.setItem(LS_TOKEN, t);
      url.searchParams.delete('token');
      history.replaceState(null, '', url.pathname + url.search + url.hash);
    }
  } catch {
    /* ignore */
  }
}

function loadDeviceId(): string {
  let id = localStorage.getItem(LS_DEVICE);
  if (!id) {
    id = newUUID();
    localStorage.setItem(LS_DEVICE, id);
  }
  return id;
}

function loadTheme(): ThemeMode {
  const raw = localStorage.getItem(LS_THEME);
  if (raw && (THEME_MODES as readonly string[]).includes(raw)) return raw as ThemeMode;
  return 'system';
}

function clampWidth(px: number): number {
  if (!Number.isFinite(px)) return SIDEBAR_WIDTH_DEFAULT;
  return Math.max(SIDEBAR_WIDTH_MIN, Math.min(SIDEBAR_WIDTH_MAX, Math.round(px)));
}

function loadSidebarWidth(): number {
  const raw = localStorage.getItem(LS_SIDEBAR_WIDTH);
  if (!raw) return SIDEBAR_WIDTH_DEFAULT;
  const n = Number.parseInt(raw, 10);
  return Number.isFinite(n) ? clampWidth(n) : SIDEBAR_WIDTH_DEFAULT;
}

adoptTokenFromUrl();
const WSS_URL = deriveWssUrl();

export const useSettingsStore = create<SettingsState>((set) => ({
  wssUrl: WSS_URL,
  token: localStorage.getItem(LS_TOKEN) ?? '',
  deviceId: loadDeviceId(),
  themeMode: loadTheme(),
  hasCredentials: Boolean(localStorage.getItem(LS_TOKEN)),
  sidebarPinned: localStorage.getItem(LS_SIDEBAR_PINNED) === '1',
  sidebarWidth: loadSidebarWidth(),

  setToken: (token) => {
    const trimmed = token.trim();
    if (trimmed) localStorage.setItem(LS_TOKEN, trimmed);
    else localStorage.removeItem(LS_TOKEN);
    set({ token: trimmed, hasCredentials: Boolean(trimmed) });
  },

  clearToken: () => {
    localStorage.removeItem(LS_TOKEN);
    set({ token: '', hasCredentials: false });
  },

  setThemeMode: (mode) => {
    localStorage.setItem(LS_THEME, mode);
    set({ themeMode: mode });
  },

  setSidebarPinned: (pinned) => {
    if (pinned) localStorage.setItem(LS_SIDEBAR_PINNED, '1');
    else localStorage.removeItem(LS_SIDEBAR_PINNED);
    set({ sidebarPinned: pinned });
  },

  setSidebarWidth: (px) => {
    const next = clampWidth(px);
    localStorage.setItem(LS_SIDEBAR_WIDTH, String(next));
    set({ sidebarWidth: next });
  },

  loadActiveAgent: () => localStorage.getItem(LS_ACTIVE_AGENT),
  saveActiveAgent: (agentId) => localStorage.setItem(LS_ACTIVE_AGENT, agentId),
}));
